// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — the table games on screen (`docs/AYNI_XR_MEETING_GAMES_PLAN.md`
 * §5): what a player sees of their table's Wayra's Adventures or Team Earth,
 * drawn from the engine's own kiosk view, and the presenter's list of
 * tables. Markup only; `meetingGamesUI.ts` mounts it and sends what is
 * pressed (`data-gplay` buttons are the table's actions, `data-look` turns
 * the player's own globe to a place).
 *
 * The places, clues, cards and events are the engines' own content, in the
 * viewer's language (AYNI 3D wrote them in four; this site shows English
 * and Spanish). Clocks are drawn as `data-deadline` (this device's clock),
 * so the panel can count down between the room's messages.
 */

import { getLocale, t } from '../i18n'
import { renderWayraClue } from '../ayni3d/shared/wayra-game/content'
import type { WayraKioskView } from '../ayni3d/shared/wayra-game/view'
import type { CoopKioskView } from '../ayni3d/shared/coop-game/view'
import { COOP_CARDS, COOP_EVENTS, COOP_ROLE_INFO } from '../ayni3d/shared/coop-game/content'
import { COOP_ROLES, type CoopRole } from '../ayni3d/shared/coop-game/types'
import type { LocalizedString } from '../ayni3d/shared/dsa-types'
import type { TableSummary, TableView } from '../services/gameTables'
import { escapeAttr, escapeHtml } from './domUtils'

/** A text of the engines' content in the viewer's language. */
export function localText(s: LocalizedString | undefined | null): string {
  if (!s) return ''
  const r = s as Record<string, string | undefined>
  const lang = getLocale()
  return r[lang] ?? r[lang.slice(0, 2)] ?? r.en ?? ''
}

/** A deadline on the room's clock, as this device's: the room's `serverNow` arrived at `receivedAt`. */
export const localDeadline = (at: number | null, serverNow: number, receivedAt: number): number | null =>
  at === null ? null : receivedAt + (at - serverNow)

const secondsTo = (deadline: number | null, now: number): number => (deadline === null ? 0 : Math.max(0, Math.ceil((deadline - now) / 1000)))

/** A countdown the panel's clock keeps up to date. */
function clock(deadline: number | null, key: Parameters<typeof t>[0], now: number): string {
  if (deadline === null) return ''
  return `<p class="game-timer" data-deadline="${deadline}" data-key="${escapeAttr(key)}">${escapeHtml(t(key, { s: secondsTo(deadline, now) }))}</p>`
}

const stars = (n: number, of: number): string => `<span class="game-stars" aria-label="${escapeAttr(t('game.stars', { n, of }))}">${'★'.repeat(n)}${'☆'.repeat(Math.max(0, of - n))}</span>`

const action = (kind: string, label: string, extra = '', primary = false, pressed: boolean | null = null, disabled = false): string =>
  `<button type="button" class="${primary ? 'activity-primary ' : ''}game-act" data-gplay="${kind}"${extra}${pressed === null ? '' : ` aria-pressed="${String(pressed)}"`}${disabled ? ' disabled' : ''}>${label}</button>`

// ------------------------------------------------------------------ Wayra

function wayraHtml(v: WayraKioskView, receivedAt: number, now: number): string {
  const at = (x: number | null): number | null => localDeadline(x, v.serverNow, receivedAt)
  const present = v.seats.filter((s) => !s.away).length
  let html = v.stops ? `<p class="lesson-notice">${escapeHtml(t('game.wayra.stop', { n: Math.min(v.stop + 1, v.stops), of: v.stops }))}${v.kind === 'home' ? ` · ${escapeHtml(t('game.wayra.home'))}` : ''}</p>` : ''
  switch (v.phase) {
    case 'lobby':
      return html + `<p>${escapeHtml(t('game.lobby.wait'))}</p>`
    case 'sniff':
      return html + `<p class="game-prompt">${escapeHtml(t('game.wayra.sniff'))}</p>`
    case 'clues': {
      html += `<p>${escapeHtml(t('game.wayra.treats', { n: v.treats, now: v.treatsIfNow }))}</p>`
      const clues = v.clues.slice(0, v.level)
      html += `<ol class="game-clues" reversed>${[...clues].reverse().map((c, i) => `<li class="${i === 0 ? 'game-prompt' : 'game-muted'}">${escapeHtml(renderWayraClue(c, getLocale()))}</li>`).join('')}</ol>`
      if (v.lastWrong) {
        const place = v.candidates.find((c) => c.id === v.lastWrong!.placeId)
        html += `<p class="lesson-notice">${escapeHtml(t('game.wayra.wrong', { place: localText(place?.name) }))}</p>`
      }
      html += `<ul class="game-places">${v.candidates
        .map((c) => {
          const mine = v.you?.vote === c.id
          const votes = c.votes ? ` <span class="game-count">${'●'.repeat(c.votes)}</span>` : ''
          return `<li class="${c.crossedOut ? 'game-crossed' : ''}">${action('vote', `${escapeHtml(localText(c.name))} <span class="game-muted">${escapeHtml(localText(c.country))}</span>${votes}`, ` data-place="${escapeAttr(c.id)}"`, false, mine, c.crossedOut)}<button type="button" class="lesson-link game-look" data-look="${c.lat},${c.lon}" aria-label="${escapeAttr(t('game.wayra.look', { place: localText(c.name) }))}">${escapeHtml(t('game.wayra.lookShort'))}</button></li>`
        })
        .join('')}</ul>`
      if (v.lock) {
        const place = v.candidates.find((c) => c.id === v.lock!.placeId)
        html += clock(at(v.lock.locksAt), 'game.wayra.locking', now).replace('</p>', ` · ${escapeHtml(localText(place?.name))}</p>`)
        html += `<p class="lesson-notice">${escapeHtml(t('game.wayra.lockHint'))}</p>`
      } else {
        html += `<p class="lesson-notice">${escapeHtml(t('game.wayra.voteHint', { n: Math.floor(present / 2) + 1 }))}</p>`
      }
      if (v.nextClueAt !== null) html += clock(at(v.nextClueAt), 'game.wayra.nextIn', now)
      else if (v.graceEndsAt !== null) html += clock(at(v.graceEndsAt), 'game.wayra.graceIn', now)
      const want = v.seats.filter((s) => s.wantsClue).length
      html += action('clue', `${escapeHtml(t('game.wayra.clue'))}${want ? ` (${want}/${present})` : ''}`, '', false, v.you?.wantsClue ?? false)
      return html
    }
    case 'travel': {
      const r = v.result
      if (r) {
        html += r.outcome === 'found'
          ? `<p class="game-answer">${escapeHtml(t('game.wayra.found', { place: localText(r.name), n: r.treats }))}</p>`
          : `<p class="game-answer">${escapeHtml(t('game.wayra.para', { place: localText(r.name) }))}</p>`
        if (r.postcard) html += `<p class="lesson-notice">${escapeHtml(localText(r.postcard))}</p>`
      }
      const done = v.seats.filter((s) => s.done).length
      html += clock(at(v.phaseEndsAt), 'game.wayra.travelIn', now)
      html += action('next', `${escapeHtml(t('game.wayra.next'))}${done ? ` (${done}/${present})` : ''}`, '', true)
      return html
    }
    case 'finale': {
      const f = v.finale
      html = `<p class="game-answer">${escapeHtml(t('game.wayra.finale'))}</p>`
      if (f) {
        html += `<p>${stars(f.stars, 3)} ${escapeHtml(t('game.wayra.final', { n: f.treats, of: f.possible }))}</p>`
        if (v.para && f.shared === null) {
          html += `<p>${escapeHtml(t('game.wayra.share', { n: f.paraTreats }))}</p><div class="activity-actions">${action('share', escapeHtml(t('game.wayra.shareYes', { n: f.shareVotes })), ' data-yes="1"', false, v.you?.shareVote === 'share')}${action('share', escapeHtml(t('game.wayra.shareNo', { n: f.keepVotes })), ' data-yes="0"', false, v.you?.shareVote === 'keep')}</div>`
        } else if (f.shared !== null) {
          html += `<p class="lesson-notice">${escapeHtml(t(f.shared ? 'game.wayra.shared' : 'game.wayra.kept'))}</p>`
        }
      }
      if (v.trail.length) html += `<p class="lesson-notice">${escapeHtml(t('game.wayra.trail', { list: v.trail.map((s) => localText(s.name)).join(' → ') }))}</p>`
      return html
    }
    default:
      return html
  }
}

// ------------------------------------------------------------------ Team Earth

function meters(v: CoopKioskView): string {
  const p = v.planet
  if (!p) return ''
  const bar = (role: 'air' | 'water' | 'fire'): string =>
    `<li><span>${escapeHtml(localText(COOP_ROLE_INFO[role].meterLabel))}</span><span class="game-meter" role="meter" aria-valuemin="0" aria-valuemax="100" aria-valuenow="${Math.round(p[role])}"><span style="inline-size:${Math.max(0, Math.min(100, Math.round(p[role])))}%"></span></span><span class="game-count">${Math.round(p[role])}</span></li>`
  return `<ul class="game-meters">${bar('air')}${bar('water')}${bar('fire')}<li><span>${escapeHtml(localText(COOP_ROLE_INFO.climate.meterLabel))}</span><span class="game-count">${escapeHtml(t('game.earth.warming', { c: p.warmingC.toLocaleString(getLocale(), { minimumFractionDigits: 1, maximumFractionDigits: 1 }) }))}</span></li></ul>`
}

function earthHtml(v: CoopKioskView, receivedAt: number, now: number): string {
  const at = (x: number | null): number | null => localDeadline(x, v.serverNow, receivedAt)
  const present = v.seats.filter((s) => !s.away).length
  const done = v.seats.filter((s) => s.done).length
  let html = v.rounds ? `<p class="lesson-notice">${escapeHtml(t('game.earth.round', { n: Math.min(v.round + 1, v.rounds), of: v.rounds, year: v.year }))}</p>` : ''
  html += meters(v)
  const event = COOP_EVENTS.find((e) => e.id === v.eventId)
  switch (v.phase) {
    case 'lobby':
      return html + `<p>${escapeHtml(t('game.lobby.wait'))}</p>`
    case 'brief':
      if (event) html += `<p class="game-prompt">${escapeHtml(localText(event.title))}</p><p>${escapeHtml(localText(event.text))}</p>`
      html += clock(at(v.phaseEndsAt), 'game.earth.planIn', now)
      return html + action('next', `${escapeHtml(t('game.earth.toPlan'))}${done ? ` (${done}/${present})` : ''}`, '', true)
    case 'plan': {
      const lens: CoopRole = v.you?.lens ?? 'air'
      const ballot = new Set(v.you?.ballot ?? [])
      const spent = v.you?.ballotCost ?? 0
      html += `<p>${escapeHtml(t('game.earth.pool', { n: v.pool, left: Math.max(0, v.pool - spent) }))}</p>`
      html += `<div class="activity-chips" role="group" aria-label="${escapeAttr(t('game.earth.lens'))}">${COOP_ROLES.map((r) => action('lens', escapeHtml(localText(COOP_ROLE_INFO[r].meterLabel)), ` data-role="${r}"`, false, r === lens)).join('')}</div>`
      html += `<p class="lesson-notice">${escapeHtml(localText(COOP_ROLE_INFO[lens].title))}</p>`
      html += `<ul class="game-cards">${COOP_CARDS.filter((c) => c.role === lens || c.bridge === lens)
        .map((c) => {
          const on = ballot.has(c.id)
          const votes = v.vote?.tally[c.id] ?? 0
          const tooDear = !on && spent + c.cost > v.pool
          return `<li>${action('card', `<strong>${escapeHtml(localText(c.title))}</strong> <span class="game-muted">${escapeHtml(t('game.earth.cost', { n: c.cost }))}</span>${votes ? ` <span class="game-count">${'●'.repeat(votes)}</span>` : ''}<br><span class="game-muted">${escapeHtml(localText(c.why))}</span>`, ` data-id="${escapeAttr(c.id)}"`, false, on, tooDear)}</li>`
        })
        .join('')}</ul>`
      if (v.vote?.preview.length) html += `<p class="lesson-notice">${escapeHtml(t('game.earth.preview', { list: v.vote.preview.map((id) => localText(COOP_CARDS.find((c) => c.id === id)?.title)).join(' · '), n: v.vote.previewCost, of: v.pool }))}</p>`
      html += clock(at(v.phaseEndsAt), 'game.earth.planLeft', now)
      return html + action('ready', `${escapeHtml(t('game.earth.ready'))}${done ? ` (${done}/${present})` : ''}`, '', true)
    }
    case 'reveal': {
      const r = v.report
      if (r) {
        const played = r.played.map((id) => localText(COOP_CARDS.find((c) => c.id === id)?.title)).filter(Boolean)
        html += `<p class="game-prompt">${escapeHtml(played.length ? t('game.earth.played', { list: played.join(' · ') }) : t('game.earth.nothing'))}</p>`
        const delta = (k: 'air' | 'water' | 'fire'): string => {
          const d = Math.round(r.after[k] - r.before[k])
          return `${localText(COOP_ROLE_INFO[k].meterLabel)} ${d >= 0 ? '+' : ''}${d}`
        }
        html += `<p class="lesson-notice">${escapeHtml([delta('air'), delta('water'), delta('fire')].join(' · '))}</p>`
      }
      html += clock(at(v.phaseEndsAt), 'game.earth.nextIn', now)
      return html + action('next', `${escapeHtml(t('game.earth.next'))}${done ? ` (${done}/${present})` : ''}`, '', true)
    }
    case 'finale': {
      const o = v.outcome
      if (o) {
        html += `<p class="game-answer">${stars(o.stars, 4)} ${escapeHtml(t(`game.earth.path.${o.path}` as Parameters<typeof t>[0]))}</p>`
        html += `<p class="lesson-notice">${escapeHtml(t('game.earth.finale', { c: o.warmingC.toLocaleString(getLocale(), { minimumFractionDigits: 1, maximumFractionDigits: 1 }), scenario: o.scenario }))}</p>`
      }
      return html
    }
    default:
      return html
  }
}

/** A player's table, as their engine shows it; null when it has nothing to show (the game ended there). */
export function tableHtml(view: TableView, receivedAt: number, now: number = Date.now()): string | null {
  if (view.v.phase === 'none' || view.v.phase === 'ended') return null
  return view.engine === 'wayra' ? wayraHtml(view.v, receivedAt, now) : earthHtml(view.v, receivedAt, now)
}

/** The presenter's list of tables while they play. */
export function tablesHtml(engine: 'wayra' | 'earth', tables: TableSummary[]): string {
  return `<ul class="game-board-list">${tables
    .map((s) => {
      const where = s.phase === 'ended' ? t('game.tables.done') : t(engine === 'wayra' ? 'game.tables.stop' : 'game.tables.round', { n: s.at, of: s.of })
      const score = engine === 'wayra' ? t('game.tables.treats', { n: s.score }) : stars(s.stars, 4)
      return `<li class="game-board-row"><span class="game-board-name">${escapeHtml(t('game.table', { n: s.n }))} <span class="game-muted">(${escapeHtml(t('game.board.size', { n: s.size }))})</span></span><span class="game-muted">${escapeHtml(where)}</span><span class="game-board-score">${engine === 'wayra' ? escapeHtml(score) : score}</span></li>`
    })
    .join('')}</ul>`
}

/** Where a Wayra table's candidates are, for the dots on the player's globe; the place found once travelling. */
export function wayraMarks(view: TableView | null): { dots: [number, number][]; answer: [number, number] | null } {
  if (!view || view.engine !== 'wayra') return { dots: [], answer: null }
  const v = view.v
  if (v.phase === 'clues') return { dots: v.candidates.filter((c) => !c.crossedOut).map((c) => [c.lat, c.lon]), answer: null }
  if (v.phase === 'travel' && v.result) {
    const last = v.trail.at(-1)
    return { dots: [], answer: last ? [last.lat, last.lon] : null }
  }
  return { dots: [], answer: null }
}
